import { axios, organizationIdConfig } from '../configs';
import { isAxiosError } from 'axios';
import type { TCardTypeMessagePayload } from 'types/cardTypeMessage';
import { TCreateMessageForAll } from './messages';

export type TAutoReplyBusinessHourMode = 'KEYWORD_ONLY' | 'UNIFORM_AND_KEYWORD';
export type TAutoReplyOutsideHourMode = 'UNIFORM_ONLY' | 'NO_REPLY';
export type TAutoReplyMessageAssetType = 'RICH_MESSAGE' | 'CARD_TYPE_MESSAGE';
export type TAutoReplyWeekday =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export type TAutoReplyBusinessHour = {
  weekday: TAutoReplyWeekday;
  closed: boolean;
  startTime?: string;
  endTime?: string;
};

export type TAutoReplySetting = {
  id: number | null;
  organizationId: number;
  uniformReplyEnabled: boolean;
  uniformReplySections: TCreateMessageForAll[];
  uniformReplyChatLinkEnabled: boolean;
  uniformReplyChatLinkText: string | null;
  uniformReplyChatLinkLabel: string | null;
  responseTimeEnabled: boolean;
  businessHours: TAutoReplyBusinessHour[];
  businessHourMode: TAutoReplyBusinessHourMode;
  outsideHourMode: TAutoReplyOutsideHourMode;
  createdAt: string | null;
  updatedAt: string | null;
};

export type TAutoReplyKeywordRule = {
  id: number;
  organizationId: number;
  name: string;
  enabled: boolean;
  keywords: string[];
  sections: TCreateMessageForAll[];
  chatLinkEnabled: boolean;
  chatLinkText: string | null;
  chatLinkLabel: string | null;
  createdAt: string;
  updatedAt: string;
};

/** @deprecated Legacy flex payload fields — prefer imagemap fields below */
export type TRichMessageLegacyPayload = {
  title?: string;
  text?: string;
  actionUrl?: string;
  actionLabel?: string;
  aspectRatio?: string;
};

export type TRichMessageAreaActionPayload = {
  type: 'none' | 'uri' | 'message';
  label?: string;
  uri?: string;
  text?: string;
};

export type TRichMessageAreaBoundsPayload = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TRichMessageAreaPayload = {
  key: string;
  bounds: TRichMessageAreaBoundsPayload;
  action: TRichMessageAreaActionPayload;
};

export type TRichMessagePayload = TRichMessageLegacyPayload & {
  altText: string;
  imageUrl: string;
  templateId?: string;
  imagemapBaseUrl?: string;
  baseSize?: { width: number; height: number };
  areas?: TRichMessageAreaPayload[];
};

export type {
  TCardTypeAction,
  TCardTypeCard,
  TCardTypeMessagePayload,
} from 'types/cardTypeMessage';

export type TAutoReplyMessageAsset = {
  id: number;
  organizationId: number;
  type: TAutoReplyMessageAssetType;
  name: string;
  payload: TRichMessagePayload | TCardTypeMessagePayload;
  previewImageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TAssetUpdateImpact = {
  assetUpdatedAt: string;
  scenarioCount: number;
  openRunCount: number;
};

export type TAutoReplyMessageAssetUpdate = Partial<
  Omit<
    TAutoReplyMessageAsset,
    'id' | 'organizationId' | 'createdAt' | 'updatedAt'
  >
> & { expectedImpact?: TAssetUpdateImpact };

export function getAssetUpdateConfirmation(error: unknown):
  | {
      code:
        | 'ASSET_UPDATE_CONFIRMATION_REQUIRED'
        | 'ASSET_UPDATE_PREVIEW_CHANGED';
      impact: TAssetUpdateImpact;
    }
  | undefined {
  if (!isAxiosError(error) || error.response?.status !== 409) return undefined;
  const data = error.response.data as
    | { code?: unknown; impact?: Partial<TAssetUpdateImpact> }
    | undefined;
  if (
    data?.code !== 'ASSET_UPDATE_CONFIRMATION_REQUIRED' &&
    data?.code !== 'ASSET_UPDATE_PREVIEW_CHANGED'
  ) {
    return undefined;
  }
  const impact = data.impact;
  if (
    !impact ||
    typeof impact.assetUpdatedAt !== 'string' ||
    !Number.isSafeInteger(impact.scenarioCount) ||
    !Number.isSafeInteger(impact.openRunCount)
  ) {
    return undefined;
  }
  return { code: data.code, impact: impact as TAssetUpdateImpact };
}

export const getAutoReplySettings = async (): Promise<TAutoReplySetting> => {
  const response = await axios.get<TAutoReplySetting>('auto-reply/settings');
  return response.data;
};

export const upsertAutoReplySettings = async (
  payload: Omit<
    TAutoReplySetting,
    'id' | 'organizationId' | 'createdAt' | 'updatedAt'
  >,
  organizationId?: number | null,
): Promise<TAutoReplySetting> => {
  const response = await axios.put<TAutoReplySetting>(
    'auto-reply/settings',
    payload,
    organizationIdConfig(organizationId),
  );
  return response.data;
};

export const getAutoReplyKeywordRules = async (): Promise<
  TAutoReplyKeywordRule[]
> => {
  const response = await axios.get<TAutoReplyKeywordRule[]>(
    'auto-reply/keyword-rules',
  );
  return response.data;
};

export const createAutoReplyKeywordRule = async (
  payload: Omit<
    TAutoReplyKeywordRule,
    'id' | 'organizationId' | 'createdAt' | 'updatedAt'
  >,
  organizationId?: number | null,
): Promise<TAutoReplyKeywordRule> => {
  const response = await axios.post<TAutoReplyKeywordRule>(
    'auto-reply/keyword-rules',
    payload,
    organizationIdConfig(organizationId),
  );
  return response.data;
};

export const updateAutoReplyKeywordRule = async (
  id: number,
  payload: Partial<
    Omit<
      TAutoReplyKeywordRule,
      'id' | 'organizationId' | 'createdAt' | 'updatedAt'
    >
  >,
  organizationId?: number | null,
): Promise<TAutoReplyKeywordRule> => {
  const response = await axios.patch<TAutoReplyKeywordRule>(
    `auto-reply/keyword-rules/${id}`,
    payload,
    organizationIdConfig(organizationId),
  );
  return response.data;
};

export const deleteAutoReplyKeywordRule = async (
  id: number,
): Promise<{ success: boolean }> => {
  const response = await axios.delete<{ success: boolean }>(
    `auto-reply/keyword-rules/${id}`,
  );
  return response.data;
};

export const getAutoReplyMessageAssets = async (
  type?: TAutoReplyMessageAssetType,
): Promise<TAutoReplyMessageAsset[]> => {
  const response = await axios.get<TAutoReplyMessageAsset[]>(
    'auto-reply/message-assets',
    {
      params: type ? { type } : undefined,
    },
  );
  return response.data;
};

export const createAutoReplyMessageAsset = async (
  payload: Omit<
    TAutoReplyMessageAsset,
    'id' | 'organizationId' | 'createdAt' | 'updatedAt'
  >,
  organizationId?: number | null,
): Promise<TAutoReplyMessageAsset> => {
  const response = await axios.post<TAutoReplyMessageAsset>(
    'auto-reply/message-assets',
    payload,
    organizationIdConfig(organizationId),
  );
  return response.data;
};

export const updateAutoReplyMessageAsset = async (
  id: number,
  payload: TAutoReplyMessageAssetUpdate,
  organizationId?: number | null,
): Promise<TAutoReplyMessageAsset> => {
  const response = await axios.patch<TAutoReplyMessageAsset>(
    `auto-reply/message-assets/${id}`,
    payload,
    organizationIdConfig(organizationId),
  );
  return response.data;
};

export const deleteAutoReplyMessageAsset = async (
  id: number,
  organizationId?: number | null,
): Promise<{ success: boolean }> => {
  const response = await axios.delete<{ success: boolean }>(
    `auto-reply/message-assets/${id}`,
    organizationIdConfig(organizationId),
  );
  return response.data;
};
